// Uptick integration page (uptick_plan.md §6 v0.1, §14 v0.2.1). Owner/admin
// only — the credentials control the org's Uptick surface. Connect form
// performs a live token exchange server-side; the status card shows the
// permission probe; the Setup check shows what the BT1851_ ref-convention
// scan found; the document setting says where the report PDF is uploaded.

import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authClient, useSession } from "../lib/auth.ts";
import { useOrg } from "../lib/useOrg.ts";
import {
  uptickApi, uptickPushApi, type UptickStatus, type UptickProbe, type UptickMappingEntry,
  type UptickDiscovery, type UptickAttachTo, type UptickSettings,
} from "../lib/api.ts";
import { UptickMappingPanel } from "../components/UptickMappingPanel.tsx";
import { UptickSuggestionView } from "../components/UptickSuggestion.tsx";

function modePill(mode: string | null) {
  if (mode === "standard") return <span className="pill pass">Connected</span>;
  if (mode === "insufficient") return <span className="pill fail">Insufficient</span>;
  // Legacy rows may still carry "minimal" until their next Test.
  return <span className="pill">Recheck</span>;
}

function probeLine(probe: UptickProbe | null) {
  if (!probe) return null;
  // Counts drive the server-side checks (mode, zero-tasks warning) but the
  // display is ticks only — row totals of a customer's book aren't something
  // to print on a settings page.
  const label = (v: UptickProbe[keyof UptickProbe]) => {
    if (v && typeof v === "object") {
      return v.readable === true ? "✓" : v.readable === false ? "✗" : "?";
    }
    return v === true ? "✓" : v === false ? "✗" : "?";
  };
  return (
    <div className="muted" style={{ fontSize: "0.9em" }}>
      Access — tasks {label(probe.tasks)} · assets {label(probe.assets)} ·
      properties {label(probe.properties)} · clients {label(probe.clients)}
    </div>
  );
}

// The published ref vocabulary (plan §14). A customer creates any subset in
// their own prompt set; the push fills whatever exists.
const REF_VOCAB: Array<[string, string, string]> = [
  ["BT1851_RESULT",       "Assertion", "Pass / Fail (a fail carries the test name + report link as the reason)"],
  ["BT1851_CAPACITY_AH",  "Decimal",   "delivered capacity, Ah"],
  ["BT1851_CAPACITY_PCT", "Integer",   "delivered capacity, % of rated"],
  ["BT1851_RATED_AH",     "Decimal",   "rated capacity, Ah"],
  ["BT1851_END_VOLTAGE",  "Decimal",   "battery voltage at end, V"],
  ["BT1851_DURATION_MIN", "Integer",   "test duration, minutes"],
  ["BT1851_REPORT_URL",   "Text",      "link to the cloud report"],
  ["BT1851_TEST_NAME",    "Text",      "test name"],
  ["BT1851_TEST_DATE",    "Text",      "test date (YYYY-MM-DD)"],
  ["BT1851_BATTERY",      "Text",      "battery maker + model"],
];

// Results waiting for a person: arrived (auto-push on) without an Uptick
// work-order reference, or stopped because the work order already had a result
// (plan §12, push rule 1). Each links to its test page, where the push is
// completed or the replacement confirmed.
function UnmatchedTray(props: { orgId: string }) {
  const qc = useQueryClient();
  const jobs = useQuery({
    queryKey: ["org", props.orgId, "uptick-jobs", "waiting"],
    queryFn: () => uptickPushApi.jobs(props.orgId, ["unmatched", "needs_confirmation"]),
    retry: false,
  });
  // One-click send of a suggested work order (push rule 2); the push itself still applies rule 1.
  const send = useMutation({
    mutationFn: (v: { testId: string; servicetaskId: number }) => uptickPushApi.push(props.orgId, v.testId, v.servicetaskId),
    onSettled: () => qc.invalidateQueries({ queryKey: ["org", props.orgId, "uptick-jobs", "waiting"] }),
  });
  const rows = jobs.data?.jobs ?? [];
  if (rows.length === 0) return null;
  return (
    <div className="card" style={{ maxWidth: "44em", marginBottom: "1.5em" }}>
      <h4 style={{ marginTop: 0 }}>Awaiting an Uptick work order ({rows.length})</h4>
      <p className="muted" style={{ fontSize: "0.9em" }}>
        These results arrived without a work-order reference, or their work
        order already has a result. Open each one to pick the work order, or to
        confirm replacing the result already there.
      </p>
      <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
        {rows.map((j) => (
          <li key={j.test_id}>
            <Link to={`/tests/${j.test_id}`}>{j.test_name ?? j.test_id}</Link>
            {j.started_at ? <span className="muted"> · {new Date(j.started_at).toLocaleString()}</span> : null}
            {j.status === "needs_confirmation" && (
              <span className="err">
                {" "}· work order has {j.detail?.conflict?.result ?? "?"}, this test measured{" "}
                {j.detail?.conflict?.this_verdict ?? "?"} — check and confirm
              </span>
            )}
            {j.status === "unmatched" && j.suggestion && (
              <UptickSuggestionView
                suggestion={j.suggestion}
                sending={send.isPending && send.variables?.testId === j.test_id}
                onSend={(st) => send.mutate({ testId: j.test_id, servicetaskId: st })}
              />
            )}
          </li>
        ))}
      </ul>
      {send.isError && !(send.error as Error).message.startsWith("result-exists") && (
        <p className="err">{(send.error as Error).message}</p>
      )}
    </div>
  );
}

function SetupCheck(props: {
  discovery: UptickDiscovery | null;
  attachTo: UptickAttachTo;
  autoPush: boolean;
  onSettings: (v: Partial<UptickSettings>) => void;
  saving: boolean;
}) {
  const d = props.discovery;
  return (
    <div className="card" style={{ maxWidth: "44em", marginBottom: "1.5em" }}>
      <h4 style={{ marginTop: 0 }}>Setup check</h4>
      {!d && (
        <p className="muted">Not scanned yet — press <em>Test connection</em>.</p>
      )}
      {d && d.questions.length === 0 && (
        <>
          <p className="err">
            No <code>BT1851_</code> questions found in this tenant&rsquo;s
            inspection forms. Results will be pushed as Pass/Fail + performed
            date + the report PDF only.
          </p>
          <p className="muted" style={{ fontSize: "0.9em" }}>
            To receive the readings as well, an Uptick admin adds questions
            to the prompt set used for battery service tasks (a dedicated
            battery set, or the panel&rsquo;s annual section) with these
            refs — any subset — attaches the set to the battery asset type
            and routine service type, then presses <em>Test connection</em>:
          </p>
        </>
      )}
      {d && d.questions.length > 0 && (
        <>
          <p>
            Found <strong>{d.questions.length}</strong> BT1851 question{d.questions.length === 1 ? "" : "s"} in{" "}
            <strong>{d.sets.length}</strong> prompt set{d.sets.length === 1 ? "" : "s"}
            {d.sets.length > 0 ? <>: {d.sets.map((s) => s.name).join(" · ")}</> : null}.
          </p>
          <ul style={{ margin: "0 0 0.6em", paddingLeft: "1.2em", fontSize: "0.9em" }}>
            {d.questions.map((q) => (
              <li key={q.id}>
                <code>{q.ref}</code> [{q.type.toLowerCase()}]
                {q.label ? <span className="muted"> — {q.label}</span> : null}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: "0.9em" }}>
            Asset types wired to those sets:{" "}
            {d.asset_types.length > 0
              ? <strong>{d.asset_types.map((t) => t.name).join(" · ")}</strong>
              : <span className="err">none — attach the prompt set to the battery asset type, or the picker falls back to battery-named types</span>}
            .
          </p>
        </>
      )}
      {d && (
        <details style={{ fontSize: "0.9em", marginBottom: "0.6em" }}>
          <summary className="muted">Ref vocabulary</summary>
          <table style={{ fontSize: "0.95em", marginTop: "0.4em" }}>
            <tbody>
              {REF_VOCAB.map(([ref, type, what]) => (
                <tr key={ref}><td><code>{ref}</code></td><td className="muted">{type}</td><td>{what}</td></tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {d && d.truncated && (
        <p className="muted" style={{ fontSize: "0.85em" }}>
          Only the first {d.types_checked} of {d.types_total} asset types were checked.
        </p>
      )}
      {d && d.errors.length > 0 && (
        <p className="muted" style={{ fontSize: "0.85em" }}>Scan warnings: {d.errors.join(" · ")}</p>
      )}
      {d && (
        <p className="muted" style={{ fontSize: "0.85em" }}>
          Scanned {new Date(d.scanned_at).toLocaleString()}.
        </p>
      )}
      <label style={{ display: "block", marginTop: "0.6em" }}>
        <input
          type="checkbox"
          checked={props.autoPush}
          disabled={props.saving}
          onChange={(e) => props.onSettings({ auto_push: e.target.checked })}
        />{" "}
        Auto-push new results
        <span className="muted" style={{ fontSize: "0.9em" }}>
          {" "}— sent as soon as they arrive when the test carries an Uptick
          work-order reference; otherwise they wait in the tray above until
          someone picks the work order.
        </span>
      </label>
      <label style={{ display: "block", marginTop: "0.6em" }}>
        Attach the report PDF to{" "}
        <select
          value={props.attachTo}
          disabled={props.saving}
          onChange={(e) => props.onSettings({ attach_to: e.target.value as UptickAttachTo })}
        >
          <option value="both">the work order and the asset</option>
          <option value="task">the work order only (BT-1851 folder)</option>
          <option value="asset">the asset only</option>
          <option value="none">nothing — no document upload</option>
        </select>
      </label>
    </div>
  );
}

export function IntegrationsPage() {
  const qc = useQueryClient();
  const { data: session } = useSession();
  const orgId = session?.session.activeOrganizationId;
  const myUserId = session?.user?.id;
  // Active-org name shown prominently: an integration row binds to ONE org,
  // and with the switcher hidden for single-org users nothing else on screen
  // says which org is being configured.
  const { name: orgName } = useOrg();

  // Role gate mirrors OrganisationPage: membership roles come from
  // Better-Auth's full-organization read; the server enforces regardless.
  const fullOrg = useQuery({
    queryKey: ["org", orgId, "full"],
    queryFn: async () => {
      const r = await authClient.organization.getFullOrganization();
      return r.data;
    },
    enabled: !!orgId,
  });
  const myRole = (fullOrg.data?.members ?? []).find((m) => m.userId === myUserId)?.role ?? "member";
  const canManage = myRole === "owner" || myRole === "admin";

  const status = useQuery({
    queryKey: ["org", orgId, "uptick"],
    queryFn: () => uptickApi.status(orgId!),
    enabled: !!orgId && canManage,
    retry: false,
  });

  const [form, setForm] = useState({
    tenant_subdomain: "",
    client_id: "",
    client_secret: "",
    service_username: "",
    service_password: "",
  });
  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const connect = useMutation({
    mutationFn: () => uptickApi.connect(orgId!, form),
    onSuccess: () => {
      setForm((f) => ({ ...f, client_secret: "", service_password: "" }));
      qc.invalidateQueries({ queryKey: ["org", orgId, "uptick"] });
    },
  });
  const test = useMutation({
    mutationFn: () => uptickApi.test(orgId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org", orgId, "uptick"] }),
    onError: () => qc.invalidateQueries({ queryKey: ["org", orgId, "uptick"] }),
  });
  const disconnect = useMutation({
    mutationFn: () => uptickApi.disconnect(orgId!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org", orgId, "uptick"] }),
  });
  const settings = useMutation({
    mutationFn: (patch: Partial<UptickSettings>) => uptickApi.saveSettings(orgId!, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org", orgId, "uptick"] }),
  });

  if (!orgId) return null;
  if (fullOrg.isPending) return <p className="muted">Loading…</p>;
  if (!canManage) {
    return (
      <section>
        <h2>Integrations</h2>
        <p className="muted">
          Integration settings are managed by organisation owners and admins.
        </p>
      </section>
    );
  }

  const s: UptickStatus | undefined = status.data;
  const connected = s?.connected === true;

  return (
    <section>
      <h2>Integrations</h2>
      <p style={{ marginTop: "-0.4em" }}>
        Configuring for organisation: <strong>{orgName ?? "…"}</strong>
        <span className="muted" style={{ fontSize: "0.9em" }}>
          {" "}— switch organisations from the selector in the top bar if this
          isn&rsquo;t the one you mean.
        </span>
      </p>

      <h3>Uptick</h3>
      <p className="muted" style={{ maxWidth: "60em" }}>
        Connects this organisation to an Uptick tenant so battery test results
        can be pushed onto the matching work orders. Server-to-server only —
        testers never hold these credentials.{" "}
        <Link to="/integrations/uptick-guide">Setup guide</Link>
        {" · "}
        <Link to="/integrations/uptick-batteries">Batteries in Uptick</Link>
      </p>

      {connected && s.connected && (
        <div className="card" style={{ maxWidth: "44em", marginBottom: "1.5em" }}>
          <p>
            <strong>Connected</strong> to{" "}
            <code>{s.tenant_subdomain}.onuptick.com</code>{" "}
            as <code>{s.service_username}</code> · {modePill(s.mode)}
          </p>
          {probeLine(s.probe)}
          {(() => {
            const t = s.probe?.tasks;
            const props = s.probe?.properties;
            const zeroTasks = t && typeof t === "object" && t.readable === true
              && (t.count ?? 0) === 0
              && props && typeof props === "object" && (props.count ?? 0) > 0;
            return zeroTasks ? (
              <p className="err">
                The service account can see 0 work orders on a populated
                tenant — its security group is probably missing &ldquo;Can
                see all tasks&rdquo;. Fix the group in Uptick, then Test
                connection.
              </p>
            ) : null;
          })()}
          {s.mode === "insufficient" && (
            <p className="err">
              The service account cannot read tasks at all — the integration
              cannot function. Grant &ldquo;Can see all tasks&rdquo; and
              &ldquo;Can update tasks&rdquo; in its Uptick security group,
              then Test connection.
            </p>
          )}
          <p className="muted" style={{ fontSize: "0.9em" }}>
            Application <code>{s.client_id.slice(0, 10)}…</code>
            {s.scopes ? <> · scopes: {s.scopes}</> : null}
            {s.token_expires_at
              ? <> · token expires {new Date(s.token_expires_at).toLocaleString()}</>
              : null}
            {" "}· connected {new Date(s.connected_at).toLocaleDateString()}
          </p>
          {s.last_error && (
            <p className="err">
              Last error{s.last_error_at ? ` (${new Date(s.last_error_at).toLocaleString()})` : ""}:{" "}
              {s.last_error}
            </p>
          )}
          <div style={{ display: "flex", gap: "0.6em" }}>
            <button onClick={() => test.mutate()} disabled={test.isPending}>
              {test.isPending ? "Testing + scanning…" : "Test connection"}
            </button>
            <button
              className="danger"
              onClick={() => {
                if (confirm(`Disconnect Uptick from "${orgName ?? "this organisation"}"? The stored credentials will be deleted.`)) {
                  disconnect.mutate();
                }
              }}
              disabled={disconnect.isPending}
            >
              Disconnect
            </button>
          </div>
          {test.isError && <p className="err">{(test.error as Error).message}</p>}
        </div>
      )}

      {connected && s.connected && <UnmatchedTray orgId={orgId} />}
      {connected && s.connected && (
        <SetupCheck
          discovery={s.discovery}
          attachTo={s.settings?.attach_to ?? "both"}
          autoPush={s.settings?.auto_push ?? true}
          onSettings={(v) => settings.mutate(v)}
          saving={settings.isPending}
        />
      )}
      {settings.isError && <p className="err">{(settings.error as Error).message}</p>}

      {connected && s.connected && (
        <details style={{ maxWidth: "44em", marginBottom: "1.5em" }}>
          <summary className="muted">Advanced: manual question mapping (for tenants that won&rsquo;t use BT1851_ refs)</summary>
          <UptickMappingPanel
            orgId={orgId}
            initialEntries={(s.mapping?.entries as UptickMappingEntry[] | undefined) ?? []}
          />
        </details>
      )}

      <h4>{connected ? "Reconnect / update credentials" : "Connect"}</h4>
      <div className="card" style={{ maxWidth: "44em" }}>
        <p className="muted" style={{ fontSize: "0.9em" }}>
          In Uptick: Control Panel → Uptick API → Create Application (copy the
          Client ID and the one-time Client secret), and create a dedicated
          service-account user (API license) in the BT-1851 Integration
          security group — Tasks: see all + update; Properties: view;
          Clients: view; Documents: upload; nothing else. Most modern
          integrations use OAuth where you log into the vendor&rsquo;s site and
          we never see your password — Uptick supports this server-side but
          doesn&rsquo;t expose the controls to customers, so until that
          changes we use a service-account credential. It is stored encrypted,
          never logged, and should be a dedicated user (e.g.{" "}
          <code>bt1851-integration@…</code>), never a real person&rsquo;s login.
        </p>
        <form
          onSubmit={(e) => { e.preventDefault(); connect.mutate(); }}
          style={{ display: "grid", gap: "0.6em" }}
        >
          <label>
            Tenant subdomain
            <input
              value={form.tenant_subdomain}
              onChange={set("tenant_subdomain")}
              placeholder="e.g. company (for company.onuptick.com)"
              required
            />
          </label>
          <label>
            Client ID
            <input value={form.client_id} onChange={set("client_id")} required />
          </label>
          <label>
            Client secret
            <input
              type="password"
              value={form.client_secret}
              onChange={set("client_secret")}
              autoComplete="off"
              required
            />
          </label>
          <label>
            Service username
            <input value={form.service_username} onChange={set("service_username")} required />
          </label>
          <label>
            Service password
            <input
              type="password"
              value={form.service_password}
              onChange={set("service_password")}
              autoComplete="off"
              required
            />
          </label>
          <div>
            <button type="submit" disabled={connect.isPending}>
              {connect.isPending ? "Validating with Uptick…" : connected ? "Update connection" : "Connect"}
            </button>
          </div>
          {connect.isError && <p className="err">{(connect.error as Error).message}</p>}
        </form>
      </div>
    </section>
  );
}
